/**
 * Turn an axios error into a string that is safe to render.
 *
 * FastAPI answers a validation failure with `detail` as an ARRAY of objects
 * ({loc, msg, type}), not a string. Rendering that array directly gives React
 * an object child — the user sees a blank or broken error box and learns
 * nothing about which field was rejected. `detail` can also be a bare object
 * for some handlers. Only the plain-string case is safe to render as-is.
 *
 * Always returns a non-empty string so a caller can render the result
 * unconditionally without needing its own fallback.
 */
export function apiErrorMessage(error, fallback = 'An error occurred') {
  const detail = error?.response?.data?.detail

  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (!item || typeof item !== 'object') return null
        // Name the offending field when FastAPI tells us: loc is like
        // ['body', 'waist_cm'] and the first segment is just the request part.
        const field = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== 'body' && part !== 'query').join('.')
          : ''
        const msg = typeof item.msg === 'string' ? item.msg : ''
        if (!msg) return null
        return field ? `${field}: ${msg}` : msg
      })
      .filter(Boolean)

    if (messages.length) return messages.join('; ')
  }

  if (detail && typeof detail === 'object') {
    const msg = detail.msg ?? detail.message
    if (typeof msg === 'string' && msg.trim()) return msg
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message
  }

  return fallback
}

/**
 * VH-238: did the AI refuse this because the account has no credit?
 *
 * The backend answers 503 with the bare code `ai_out_of_credit` (see
 * `app/ai/anthropic_provider.py`), because the API states the condition and the UI owns the
 * words — in three languages. Rendering the raw code, which `apiErrorMessage` would do, is how
 * "AI analysis failed" became a bug report in the first place.
 */
export const AI_OUT_OF_CREDIT = 'ai_out_of_credit'

export function isOutOfCredit(error) {
  const detail = error?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() === AI_OUT_OF_CREDIT
}
